import { createSlice } from "@reduxjs/toolkit";
import _ from "lodash";
import { addRevision } from "../addRevision";
import {
  calculateMaxPermille,
  calculatePermille as calculatePermilleFunc,
} from "../calculatePermille";
import { Container, Drink } from "../db";
import getLogsFromAlcoholData from "../getLogsFromAlcoholData";
import { syncPatchImagesReducer, syncSetDataReducer } from "../sync";
import { User } from "./userSlice";

function getDistanceFromLatLonInKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) {
  var R = 6371; // Radius of the earth in km
  var dLat = deg2rad(lat2 - lat1); // deg2rad below
  var dLon = deg2rad(lon2 - lon1);
  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  var d = R * c; // Distance in km
  return d;
}

function deg2rad(deg: number) {
  return deg * (Math.PI / 180);
}

export const syncProperties = ["DrunkDrinks", "route", "events", "photos"];

export type AlcoholData = {
  permille: number;
  sessionMaxPermille: number;
  DrunkDrinks: DrunkDrink[];
  revision: number;
  route: { latitude: number; longitude: number; timestamp: number }[];
  events: Event[];
  photos: Photo[];
};

const alcoholSlice = createSlice({
  name: "alcohol",
  initialState: {
    permille: 0,
    sessionMaxPermille: 0,
    DrunkDrinks: [],
    revision: 0,
    route: [],
    events: [],
    photos: [],
  } as AlcoholData,
  reducers: {
    calculatePermille: (state, action) => {
      const { user }: { user: User } = action.payload;
      const { DrunkDrinks } = _.cloneDeep(state);
      state.permille = calculatePermilleFunc({ DrunkDrinks, user });
      state.sessionMaxPermille = calculateMaxPermille({ DrunkDrinks, user });
    },
    addDrink: (state, action) => {
      const {
        drink,
        container,
        photoData,
        latitude,
        longitude,
        date,
        id,
      }: {
        drink: Drink;
        container: Container;
        photoData: string;
        latitude: number;
        longitude: number;
        date: number;
        id: string;
      } = action.payload;
      state.DrunkDrinks.push({
        id,
        drink,
        container,
        date,
        photoData,
        latitude,
        longitude,
      });

      state.DrunkDrinks.sort((a: DrunkDrink, b: DrunkDrink) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });

      addRevision(state);
    },
    deleteDrink: (state, action) => {
      const id = action.payload;
      state.DrunkDrinks = state.DrunkDrinks.filter(
        (drunkDrink) => drunkDrink.id !== id
      );

      if (getLogsFromAlcoholData(state).length === 0) state.route = [];

      addRevision(state);
    },
    resetAlcoholData: (state) => {
      state.DrunkDrinks = [];
      state.events = [];
      state.route = [];
      state.photos = [];
      state.sessionMaxPermille = 0;
      state.permille = 0;

      addRevision(state);
    },
    deleteAlcoholDataFromDay: (state, action) => {
      const date = action.payload.date;
      state.DrunkDrinks = state.DrunkDrinks.filter(
        (drunkDrink) =>
          new Date(drunkDrink.date).toLocaleDateString("cs") !== date
      );
      state.events = state.events.filter(
        (event) => new Date(event.date).toLocaleDateString("cs") !== date
      );
      state.photos = state.photos.filter(
        (photo) => new Date(photo.date).toLocaleDateString("cs") !== date
      );
      state.route = state.route.filter(
        (route) => new Date(route.timestamp).toLocaleDateString("cs") !== date
      );

      const logs = getLogsFromAlcoholData(state);
      state.route = state.route.filter((route) =>
        logs.some(
          (log) =>
            new Date(log.data.date).toLocaleDateString("cs") ===
            new Date(route.timestamp).toLocaleDateString("cs")
        )
      );

      addRevision(state);
    },
    addEvent: (state, action) => {
      const { id, type, photoData, latitude, longitude, customName, date } =
        action.payload;
      state.events.push({
        id,
        date,
        type,
        photoData,
        latitude,
        longitude,
        customName,
      });

      state.events.sort((a: Event, b: Event) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });

      addRevision(state);
    },
    deleteEvent: (state, action) => {
      const id = action.payload;
      state.events = state.events.filter((event) => event.id !== id);

      addRevision(state);
    },
    addPhoto: (state, action) => {
      const { id, photoData, latitude, longitude, date } = action.payload;
      state.photos.push({
        id,
        photoData,
        date,
        latitude,
        longitude,
      });

      state.events.sort((a: Event, b: Event) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });

      addRevision(state);
    },
    deletePhoto: (state, action) => {
      const id = action.payload;
      state.photos = state.photos.filter((photo) => photo.id !== id);

      addRevision(state);
    },
    syncPatchImages: syncPatchImagesReducer,
    syncSetData: syncSetDataReducer(syncProperties, "alcohol-data"),
    addRoutePoint: (state, action) => {
      const { latitude, longitude, timestamp } = action.payload;
      if (!latitude || !longitude) return;
      const lastLocation = state.route[state.route.length - 1];
      if (
        lastLocation &&
        getDistanceFromLatLonInKm(
          latitude,
          longitude,
          lastLocation.latitude,
          lastLocation.longitude
        ) < 0.02
      )
        return;
      if (state.route.length < 500 && getLogsFromAlcoholData(state).length > 0)
        state.route.push({
          latitude,
          longitude,
          timestamp: timestamp ?? new Date().getTime(),
        });

      addRevision(state);
    },
  },
});

export const convertVolumeFromMl = (
  volume: number,
  toUnits: string
): number => {
  switch (toUnits) {
    case "l":
      return volume / 1000;
    case "cl":
      return volume / 10;
    default:
      return volume;
  }
};

export const convertVolumeToMl = (
  volume: number,
  fromUnits: string
): number => {
  switch (fromUnits) {
    case "l":
      return volume * 1000;
    case "cl":
      return volume * 10;
    default:
      return volume;
  }
};

export type DrunkDrink = {
  id: string;
  drink: Drink;
  container: Container;
  date: number;
  photoData: string;
  latitude: number;
  longitude: number;
};

export type Event = {
  id: string;
  date: number;
  type: string;
  photoData: string;
  latitude: number;
  longitude: number;
  customName?: string;
};

export type Photo = {
  id: string;
  photoData: string;
  date: number;
  latitude: number;
  longitude: number;
};

export const {
  calculatePermille,
  addDrink,
  deleteDrink,
  syncSetData,
  syncPatchImages,
  resetAlcoholData,
  deleteAlcoholDataFromDay,
  addRoutePoint,
  addEvent,
  deleteEvent,
  addPhoto,
  deletePhoto,
} = alcoholSlice.actions;

export const initialState = alcoholSlice.getInitialState();

export default alcoholSlice.reducer;
